// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser]         = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) loadProfile(session.user);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) loadProfile(session.user);
      else { setUser(null); setCustomer(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (authUser) => {
    try {
      setUser(authUser);
      // Load or create customer profile
      let { data: cust } = await supabase
        .from('grocery_store_customers')
        .select('*, grocery_loyalty_tiers(*)')
        .eq('user_id', authUser.id)
        .single();

      if (!cust) {
        // Get Bronze tier
        const { data: bronzeTier } = await supabase
          .from('grocery_loyalty_tiers')
          .select('id')
          .eq('tier_name', 'Bronze')
          .single();

        const { data: newCust } = await supabase
          .from('grocery_store_customers')
          .insert({
            user_id:    authUser.id,
            email:      authUser.email,
            full_name:  authUser.user_metadata?.full_name || authUser.email.split('@')[0],
            tier_id:    bronzeTier?.id || null,
          })
          .select('*, grocery_loyalty_tiers(*)')
          .single();
        cust = newCust;
      }

      setCustomer(cust);
    } catch (err) {
      setUser(authUser);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCustomer(null);
  };

  const refreshCustomer = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('grocery_store_customers')
      .select('*, grocery_loyalty_tiers(*)')
      .eq('user_id', user.id)
      .single();
    if (data) setCustomer(data);
  };

  return (
    <AuthContext.Provider value={{ user, customer, loading, signIn, signUp, signOut, refreshCustomer }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
